import { mapState } from 'vuex';
import { Maybe } from 'monet';

// This mixin contains the logic for reading the token and loading the user which is only required by top-level widget pages (i.e. Settings.vue)
// Locally we can't trigger sub-widgets from top-level widgets, instead - you can temporarily attach this mixin to any subwidget and setup a route in router/widgets.js to access them directly.
// (just pass ?token=validsessiontoken in the URL)
export default {
    data() {
        return {
            activeSubscriptions: [],
            hasLoaded: false
        }
    },
    beforeMount() {
        // Grab reference to current component so we can use 'this' inside the widget message callback
        var $this = this;

        let _widgetInstance = new SC.Widget({
            onMessage: this.widgetMessageHandler
        });

        // If the widget object doesn't have an object - we aren't inside smartcharts so we wanna load the data here for local testing
        if (typeof _widgetInstance.id == 'undefined') {
            this.$store.commit('Widget/token', this.$route.query.token);
            this.loadData();
        }

        this.$store.commit('Widget/setInstance', _widgetInstance);
    },
    methods: {
        closeSelf() {
            // Causes the dialog to close, and sends the parent an SC.Error object
            // containing a myResultData block. The caller receives this as the EV.result.myResultData in its dialog callback function
            this._widgetInstance.SendDialogResult(
                new SC.Error({ code: SC.errors.ErrorCodes.NOERROR })
            );
        },

        async loadData(settings = {}) {
            // Either use a user instance passed from a widget or request a fresh instance
            if (settings.user) {
                this.$store.commit('Widget/setUser', settings.user);
                this.$store.dispatch('setLanguage', settings.user.language, { root: true });
            } else {
                await this.$store.dispatch('Widget/getUser');
            }

            // Once all api requests have finished loading, we can set the flag to render the widget
            if (window.isSmartCharts) {
                 // Any async calls we need to make before hiding the loader should be put into this requests array
                let promises = [];
                promises.push(this.$store.dispatch('Widget/getSubscriptions'))
                promises.push(this.$store.dispatch('Widget/getPaymentAttempts'));

                Promise.all(promises).then(() => {
                    this.hasLoaded = true;
                });
            } else {
                this.hasLoaded = true;
            }
        },

        /**
         * Callback that receives messages from any popups/subwidgets/fxblue itself.
         * @param  EV - the event information. This includes all the objects and things documented in section 3
         * @link https://trello.com/c/fQ1c4y6l/1-latest-sc2-framework-doc
         */
        widgetMessageHandler(EV) {
            let MyWidget = this._widgetInstance;

            // Read the token from the query string, or from smartcharts widget object - failing that, show the error screen.
            if (this.$route.query.token) {
                this.$store.commit('Widget/token', this.$route.query.token);
            } else if (typeof MyWidget.Licence != 'undefined' && MyWidget.Licence.evolutedSessionToken && MyWidget.Licence.evolutedSessionToken.length) {
                this.$store.commit('Widget/token', MyWidget.Licence.evolutedSessionToken);
            } else {
                this.$store.dispatch('Widget/widgetError');
            }

            // Dialog has been closed
            if (EV.is(SC.events.EventTypes.WIDGET_DIALOGRESULT) && EV.result) {
                this.hasLoaded = false;
                this.loadData();
            }

            // on Widget Init show the main content
            if (EV.is(SC.events.EventTypes.WIDGET_INIT)) {
                if (!EV.settings) return;
                // Display page as a widget or top-level page based on the flag passed through the widget framework
                if (EV.settings.showAsDialog) {
                    Maybe.fromNull(document.getElementById('MyDialogContent')).map(el => el.style.display = 'flex');
                    Maybe.fromNull(document.getElementById('MyWidgetContent')).map(el => el.style.display = 'none');
                } else {
                    Maybe.fromNull(document.getElementById('MyWidgetContent')).map(el => el.style.display = 'flex');
                    Maybe.fromNull(document.getElementById('MyDialogContent')).map(el => el.style.display = 'none');
                }

                // Reveal which dialog we want to render based on what we've passed to the CreateDialog function
                if (typeof EV.settings.vueComponent != 'undefined') {
                    this.activeDialog = EV.settings.vueComponent;
                }

                return this.loadData(EV.settings);
            }

            // A popup has called SaveState() - we can retrieve any data they pass here via _widgetInstance.categorySettings
            if (EV.is(SC.events.EventTypes.CATEGORYSETTINGSCHANGE)) {
                if (MyWidget.categorySettings.reloadUser) {
                    // Fetch user and reset the reload user flag
                    this.$store.dispatch('Widget/getUser')
                        .then(() => MyWidget.SaveState({reloadUser: null}));
                }

                if (window.isSmartCharts && MyWidget.categorySettings.reloadSubscriptions) {
                    // Fetch user and reset the reload user flag
                    this.$store.dispatch('Widget/getSubscriptions')
                        .then(() => MyWidget.SaveState({reloadSubscriptions: null}));
                }
            }
        },

        /**
         * A wrapper around the widget framework function for re-loading the current page as a popup modal - and swapping out the content for a vue component.
         * @param string vueComponentName - The name of the vue component to be loaded as a dialog
         * @param object dialogSettings - The settings that control the display of the dialog, as provided in the widget documentation
         * @param object extra - Any additional data that needs passing to the popup, since state does not get persisted (these will be accessible on the widget instance)
         * @param function callback - listen for any events made by the popup widget
         */
        loadWidgetDialog(vueComponentName, dialogSettings = {}, extra = {}, listener = null) {
            this._widgetInstance.CreateDialog({
                noResize: true,
                noCancel: false,
                ...dialogSettings,
                type: this._widgetInstance.type,
                settings: {
                    user: this.user,
                    ...extra,
                    showAsDialog: true,
                    vueComponent: vueComponentName
                }
            }, listener);
        },

        setHasLoaded(bool) {
            this.hasLoaded = bool;
        }

    },
    watch: {
        subscriptions(value) {
            if(this.subscriptions.length > 0) {
                this.activeSubscriptions = this.subscriptions.filter(subscription => subscription.subscriptionStatus == 'Subscribed');
            }
        }
    },
    computed: {
        ...mapState('Widget', ['user', '_widgetInstance', 'subscriptions']),
        isSmartCharts: function () {
            return window.isSmartCharts;
        },
    }
}



// WEBPACK FOOTER //
// ./resources/assets/js/mixins/widgets/topLevel.js